import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  Help as QuestionIcon,
  Star as StarIcon,
  Image as ImageIcon,
  Comment as CommentIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import formService from '../../services/formService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface Question {
  id: number;
  categoria_id: number;
  seccion_id: number;
  texto: string;
  descripcion?: string;
  tipo: 'yes_no' | 'scale' | 'text' | 'number' | 'select' | 'multiple' | 'date' | 'time';
  obligatoria: boolean;
  ponderacion: number;
  orden: number;
  opciones?: Array<{ value: string; label: string; puntos: number }>;
  config?: {
    min?: number;
    max?: number;
    paso?: number;
    unidad?: string;
  };
  puntos_si?: number;
  puntos_no?: number;
  puntos_na?: number;
  permite_evidencia: boolean;
  permite_comentario: boolean;
}

interface Category {
  id: number;
  nombre: string;
  descripcion?: string;
  orden: number;
  color: string;
  icono?: string;
}

interface Section {
  id: number;
  nombre: string;
  descripcion?: string;
  orden: number;
  color: string;
}

interface FormData {
  id: number;
  nombre: string;
  descripcion?: string;
  version: number;
  es_template: boolean;
  activo: boolean;
  total_categorias: number;
  total_preguntas: number;
  total_secciones: number;
}

const QUESTION_TYPES: Record<string, string> = {
  yes_no: 'Sí / No / N/A',
  scale: 'Escala Numérica',
  text: 'Texto Libre',
  number: 'Número',
  select: 'Selección Única',
  multiple: 'Selección Múltiple',
  date: 'Fecha',
  time: 'Hora'
};

const FormPreview: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [expandedCategory, setExpandedCategory] = useState<number | false>(false);

  useEffect(() => {
    loadFormData();
  }, [id]);

  const loadFormData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Cargar formulario
      const formResponse = await formService.getById(Number(id));
      const formInfo = formResponse.data || formResponse;
      
      // Mapear propiedades del backend (inglés) a frontend (español)
      const mappedFormData = {
        id: formInfo.id,
        nombre: formInfo.name,
        descripcion: formInfo.description,
        version: formInfo.version,
        es_template: formInfo.is_template,
        activo: formInfo.active,
        total_categorias: formInfo.total_categories || 0,
        total_preguntas: formInfo.total_questions || 0,
        total_secciones: formInfo.total_sections || 0
      };
      setFormData(mappedFormData);

      // Cargar secciones
      const sectionsResponse = await formService.getSections(Number(id));
      console.log('Sections response:', sectionsResponse);
      const sectionsData = sectionsResponse.sections || sectionsResponse.data || (Array.isArray(sectionsResponse) ? sectionsResponse : []);
      console.log('Sections data:', sectionsData);
      
      const mappedSections = sectionsData.map((sec: any) => ({
        id: sec.id,
        nombre: sec.name,
        descripcion: sec.description,
        orden: sec.order,
        color: sec.color
      }));
      setSections(mappedSections.sort((a: Section, b: Section) => a.orden - b.orden));
      console.log('Mapped sections:', mappedSections);

      // Cargar categorías
      const categoriesResponse = await formService.getCategories(Number(id));
      console.log('Categories response:', categoriesResponse);
      // Categories devuelve un array directo
      const categoriesData = Array.isArray(categoriesResponse) ? categoriesResponse : (categoriesResponse.data || []);
      console.log('Categories data:', categoriesData);
      
      // Mapear propiedades del backend (inglés) a frontend (español)
      const mappedCategories = categoriesData.map((cat: any) => ({
        id: cat.id,
        nombre: cat.name,
        descripcion: cat.description,
        orden: cat.order,
        color: cat.color,
        icono: cat.icon
      }));
      setCategories(mappedCategories.sort((a: Category, b: Category) => a.orden - b.orden));
      console.log('Mapped categories:', mappedCategories);

      // Cargar preguntas
      const questionsResponse = await formService.getQuestions(Number(id));
      console.log('Questions response:', questionsResponse);
      // Questions devuelve un objeto con .data
      const questionsData = questionsResponse.data || (Array.isArray(questionsResponse) ? questionsResponse : []);
      console.log('Questions data:', questionsData);
      
      // Mapear propiedades del backend (inglés) a frontend (español)
      const mappedQuestions = questionsData.map((q: any) => ({
        id: q.id,
        categoria_id: q.category_id,
        seccion_id: q.section_id,
        texto: q.text,
        descripcion: q.description,
        tipo: q.type,
        obligatoria: q.required,
        orden: q.order,
        ponderacion: q.weighting,
        opciones: q.options,
        puntos_si: q.config?.puntos_si ?? 0,
        puntos_no: q.config?.puntos_no ?? 0,
        puntos_na: q.config?.puntos_na ?? 0,
        permite_evidencia: q.allows_evidence,
        permite_comentario: q.allows_comment
      }));
      setQuestions(mappedQuestions.sort((a: Question, b: Question) => a.orden - b.orden));
      console.log('Mapped questions:', mappedQuestions);

    } catch (err: any) {
      console.error('Error loading form:', err);
      setError(err.response?.data?.message || 'Error al cargar el formulario');
    } finally {
      setLoading(false);
    }
  };

  const getQuestionsByCategory = (categoryId: number): Question[] => {
    return questions.filter(q => q.categoria_id === categoryId);
  };

  const getQuestionsBySection = (sectionId: number): Question[] => {
    return questions.filter(q => q.seccion_id === sectionId);
  };

  const getCategoryNameById = (categoryId: number): string => {
    const category = categories.find(c => c.id === categoryId);
    return category?.nombre || 'Sin categoría';
  };

  // Función para crear gráfico circular en canvas y convertirlo a imagen
  const createPieChart = (data: Array<{ label: string; value: number; color: string }>, title: string): string => {
    console.log(`[createPieChart] Creating chart "${title}" with data:`, data);
    
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 500;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('[createPieChart] No canvas context available');
      return '';
    }

    const centerX = 200;
    const centerY = 180;
    const radius = 120;
    
    // Título
    ctx.font = 'bold 16px Arial';
    ctx.fillStyle = '#000';
    ctx.textAlign = 'center';
    ctx.fillText(title, centerX, 30);

    // Calcular total - solo valores mayores a 0
    const validData = data.filter(item => item.value > 0);
    console.log(`[createPieChart] Valid data (value > 0):`, validData);
    
    if (validData.length === 0) {
      console.warn(`[createPieChart] No valid data for "${title}"`);
      return '';
    }
    
    const total = validData.reduce((sum, item) => sum + item.value, 0);
    console.log(`[createPieChart] Total value: ${total}`);
    
    if (total === 0) {
      console.warn(`[createPieChart] Total is 0 for "${title}"`);
      return '';
    }
    
    // Dibujar gráfico circular
    let currentAngle = -Math.PI / 2; // Empezar desde arriba
    
    validData.forEach(item => {
      const sliceAngle = (item.value / total) * 2 * Math.PI;
      
      // Dibujar slice
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + sliceAngle);
      ctx.closePath();
      ctx.fillStyle = item.color;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Dibujar porcentaje en el centro del slice
      const percentage = ((item.value / total) * 100).toFixed(1);
      if (parseFloat(percentage) > 5) { // Solo mostrar si es mayor al 5%
        const midAngle = currentAngle + sliceAngle / 2;
        const textX = centerX + (radius * 0.6) * Math.cos(midAngle);
        const textY = centerY + (radius * 0.6) * Math.sin(midAngle);
        
        ctx.font = 'bold 12px Arial';
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${percentage}%`, textX, textY);
      }
      
      currentAngle += sliceAngle;
    });

    // Leyenda debajo
    const legendY = centerY + radius + 40;
    const legendItemHeight = 20;
    const legendStartY = legendY;
    
    validData.forEach((item, index) => {
      const y = legendStartY + (index * legendItemHeight);
      
      // Cuadrado de color
      ctx.fillStyle = item.color;
      ctx.fillRect(30, y - 10, 15, 15);
      
      // Texto
      ctx.font = '11px Arial';
      ctx.fillStyle = '#000';
      ctx.textAlign = 'left';
      ctx.fillText(`${item.label}: ${item.value.toFixed(1)}%`, 50, y);
    });

    const dataUrl = canvas.toDataURL('image/png');
    console.log(`[createPieChart] Chart "${title}" generated successfully, length: ${dataUrl.length}`);
    return dataUrl;
  };

  const exportToPDF = () => {
    if (!formData) return;

    console.log('=== DEBUG PDF Export ===');
    console.log('Sections:', sections);
    console.log('Categories:', categories);
    console.log('Questions:', questions.map(q => ({ id: q.id, section_id: q.seccion_id, category_id: q.categoria_id })));

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPosition = 20;

    // Título del documento
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(formData.nombre, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;

    // Descripción
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    if (formData.descripcion) {
      const descLines = doc.splitTextToSize(formData.descripcion, pageWidth - 40);
      doc.text(descLines, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += descLines.length * 5 + 5;
    }

    // Información general
    doc.setFontSize(9);
    doc.text(`Versión: ${formData.version}  |  ${formData.es_template ? 'Plantilla' : 'Formulario'}  |  ${formData.activo ? 'Activo' : 'Inactivo'}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;

    // Cuadro de composición de puntaje
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Composición de Puntaje', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;

    // Calcular ponderación total del formulario (suma de todas las ponderaciones)
    const totalPonderacionFormulario = questions.reduce((sum, q) => sum + parseFloat(String(q.ponderacion || 0)), 0);
    
    // Datos para gráfico por categorías
    console.log('\n=== CATEGORÍAS ===' );
    console.log('Total categories:', categories.length);
    const categoryChartData = categories.map(category => {
      const categoryQuestions = getQuestionsByCategory(category.id);
      const ponderacionCategoria = categoryQuestions.reduce((sum, q) => sum + parseFloat(String(q.ponderacion || 0)), 0);
      const percentage = totalPonderacionFormulario > 0 ? (ponderacionCategoria / totalPonderacionFormulario) * 100 : 0;
      
      console.log(`Category "${category.nombre}": ${categoryQuestions.length} questions, ${ponderacionCategoria} pts, ${percentage.toFixed(1)}%`);
      
      return {
        label: category.nombre,
        value: percentage,
        color: category.color
      };
    });

    // Datos para gráfico por secciones
    console.log('\n=== SECCIONES ===' );
    console.log('Total sections:', sections.length);
    const sectionChartData = sections.map(section => {
      const sectionQuestions = getQuestionsBySection(section.id);
      const ponderacionSeccion = sectionQuestions.reduce((sum, q) => sum + parseFloat(String(q.ponderacion || 0)), 0);
      const percentage = totalPonderacionFormulario > 0 ? (ponderacionSeccion / totalPonderacionFormulario) * 100 : 0;
      
      console.log(`Section "${section.nombre}" (id: ${section.id}): ${sectionQuestions.length} questions, ${ponderacionSeccion} pts, ${percentage.toFixed(1)}%`);
      
      return {
        label: section.nombre,
        value: percentage,
        color: section.color
      };
    });

    // Crear gráficos circulares solo si hay datos
    console.log('\n=== GENERACIÓN DE GRÁFICOS ===');
    console.log('Category chart data:', categoryChartData);
    console.log('Section chart data:', sectionChartData);
    
    const categoryChart = createPieChart(categoryChartData, 'Por Categorías');
    const sectionChart = createPieChart(sectionChartData, 'Por Secciones');

    console.log('\n=== RESULTADO GENERACIÓN ===');
    console.log('Category chart generated:', categoryChart ? `YES (${categoryChart.substring(0, 50)}...)` : 'NO');
    console.log('Section chart generated:', sectionChart ? `YES (${sectionChart.substring(0, 50)}...)` : 'NO');

    // Insertar gráficos lado a lado si se generaron
    if (categoryChart || sectionChart) {
      console.log('Inserting charts into PDF...');
      const chartWidth = 90;
      const chartHeight = 112.5; // Mantener proporción 400x500 (90 * 500/400 = 112.5)
      const spacing = 10;
      const leftChartX = (pageWidth - (chartWidth * 2 + spacing)) / 2;
      const rightChartX = leftChartX + chartWidth + spacing;

      if (categoryChart) {
        console.log(`Adding category chart at position (${leftChartX}, ${yPosition})`);
        doc.addImage(categoryChart, 'PNG', leftChartX, yPosition, chartWidth, chartHeight);
      }
      if (sectionChart) {
        console.log(`Adding section chart at position (${rightChartX}, ${yPosition})`);
        doc.addImage(sectionChart, 'PNG', rightChartX, yPosition, chartWidth, chartHeight);
      }

      yPosition += chartHeight + 12;
      console.log('Charts inserted successfully');
    } else {
      console.warn('NO CHARTS GENERATED - Skipping chart section');
    }

    // Estadísticas generales (cuadro estético, ancho completo y centrado)
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Estadísticas Generales', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 8;

    const statsData = [
      ['Total de Secciones', formData.total_secciones.toString()],
      ['Total de Categorías', formData.total_categorias.toString()],
      ['Total de Preguntas', formData.total_preguntas.toString()],
      ['Preguntas Obligatorias', questions.filter(q => q.obligatoria).length.toString()],
      ['Con Evidencia Fotográfica', questions.filter(q => q.permite_evidencia).length.toString()],
      ['Con Espacio para Comentarios', questions.filter(q => q.permite_comentario).length.toString()]
    ];

    autoTable(doc, {
      startY: yPosition,
      body: statsData,
      theme: 'grid',
      headStyles: { fillColor: [76, 175, 80], fontSize: 10, fontStyle: 'bold' },
      styles: { 
        fontSize: 9, 
        cellPadding: 3,
        lineColor: [200, 200, 200],
        lineWidth: 0.1
      },
      columnStyles: {
        0: { fontStyle: 'bold', fillColor: [245, 245, 245], halign: 'left', cellWidth: 'auto' },
        1: { halign: 'center', cellWidth: 40, fillColor: [255, 255, 255] }
      },
      margin: { left: 15, right: 15 }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;

    // Detalle por sección - Iniciar en nueva página
    doc.addPage();
    yPosition = 20;
    
    console.log('\n=== DETALLE POR SECCIÓN ===');
    console.log('Total sections to process:', sections.length);
    console.log('Sections array:', sections.map(s => ({ id: s.id, nombre: s.nombre, orden: s.orden })));
    
    sections.forEach((section, idx) => {
      const sectionQuestions = getQuestionsBySection(section.id);
      console.log(`\nProcessing Section ${idx + 1}/${sections.length}: "${section.nombre}" (id: ${section.id})`);
      console.log(`  - Questions found: ${sectionQuestions.length}`);
      
      // Saltar sección si no tiene preguntas
      if (sectionQuestions.length === 0) {
        console.log(`  ⚠️ Skipping section "${section.nombre}" - no questions`);
        return;
      }
      
      console.log(`  ✓ Rendering section "${section.nombre}" with ${sectionQuestions.length} questions`);
      
      // Nueva página si no hay espacio
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setFillColor(section.color);
      doc.rect(14, yPosition - 4, 4, 6, 'F');
      doc.text(`${section.orden}. ${section.nombre}`, 20, yPosition);
      yPosition += 7;

      if (section.descripcion) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'italic');
        doc.text(section.descripcion, 20, yPosition);
        yPosition += 5;
      }

      // Tabla de preguntas con todas las columnas solicitadas: #, Categoría, Pregunta, Tipo, Pond., Oblig., Evid., Coment., Configuración
      const questionRows = sectionQuestions.map((q, idx) => {
        let config = '';
        if (q.tipo === 'yes_no') {
          config = `Sí:${q.puntos_si} / No:${q.puntos_no} / NA:${q.puntos_na}`;
        } else if (q.tipo === 'scale') {
          config = `Rango: ${q.config?.min}-${q.config?.max}`;
        } else if (q.tipo === 'number') {
          config = `${q.config?.min}-${q.config?.max} ${q.config?.unidad || ''}`;
        } else if (q.tipo === 'select' || q.tipo === 'multiple') {
          config = `${q.opciones?.length || 0} opciones`;
        }

        return [
          (idx + 1).toString(),
          getCategoryNameById(q.categoria_id),
          q.texto,
          QUESTION_TYPES[q.tipo],
          `${q.ponderacion}%`,
          q.obligatoria ? 'SI' : 'NO',
          q.permite_evidencia ? 'SI' : 'NO',
          q.permite_comentario ? 'SI' : 'NO',
          config
        ];
      });

      console.log(`  - Preparing table with ${questionRows.length} rows`);

      autoTable(doc, {
        startY: yPosition,
        head: [['#', 'Categoría', 'Pregunta', 'Tipo', 'Pond.', 'Oblig.', 'Evid.', 'Coment.', 'Configuración']],
        body: questionRows,
        theme: 'grid',
        headStyles: { fillColor: [100, 100, 100], fontSize: 7, fontStyle: 'bold', halign: 'center' },
        styles: { fontSize: 6, cellPadding: 1.5, halign: 'center' },
        columnStyles: {
          0: { cellWidth: 6 },
          1: { cellWidth: 18, halign: 'left' },
          2: { cellWidth: 'auto', halign: 'left' },
          3: { cellWidth: 16 },
          4: { cellWidth: 10 },
          5: { cellWidth: 10 },
          6: { cellWidth: 10 },
          7: { cellWidth: 10 },
          8: { cellWidth: 28, halign: 'left' }
        },
        margin: { left: 8, right: 8 }
      });

      yPosition = (doc as any).lastAutoTable.finalY + 8;
      console.log(`  ✓ Table rendered successfully for section "${section.nombre}"`);
    });
    
    console.log('\n=== PDF GENERATION COMPLETED ===');

    // Pie de página en todas las páginas
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Página ${i} de ${totalPages}  |  Generado: ${new Date().toLocaleDateString('es-ES')}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    // Guardar PDF
    doc.save(`formulario-${formData.nombre.replace(/\s+/g, '-').toLowerCase()}.pdf`);
  };

  const renderQuestionConfig = (question: Question) => {
    switch (question.tipo) {
      case 'yes_no':
        return (
          <Box mt={1}>
            <Typography variant="caption" color="textSecondary" display="block">
              Puntuación:
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
              <Chip label={`Sí: ${question.puntos_si} pts`} size="small" color="success" variant="outlined" />
              <Chip label={`No: ${question.puntos_no} pts`} size="small" color="error" variant="outlined" />
              <Chip label={`N/A: ${question.puntos_na} pts`} size="small" color="default" variant="outlined" />
            </Box>
          </Box>
        );
      
      case 'scale':
        return (
          <Box mt={1}>
            <Typography variant="caption" color="textSecondary">
              Escala: {question.config?.min} - {question.config?.max}
              {question.config?.paso && ` (Paso: ${question.config.paso})`}
            </Typography>
          </Box>
        );
      
      case 'number':
        return (
          <Box mt={1}>
            <Typography variant="caption" color="textSecondary">
              Rango: {question.config?.min} - {question.config?.max}
              {question.config?.unidad && ` ${question.config.unidad}`}
            </Typography>
          </Box>
        );
      
      case 'select':
      case 'multiple':
        return (
          <Box mt={1}>
            <Typography variant="caption" color="textSecondary" display="block">
              Opciones:
            </Typography>
            <List dense sx={{ pl: 2 }}>
              {question.opciones?.map((opt, idx) => (
                <ListItem key={idx} sx={{ py: 0 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body2">
                        • {opt.label} ({opt.puntos} pts)
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !formData) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'No se pudo cargar el formulario'}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/forms')}>
          Volver a Formularios
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box mb={3}>
        {/* Desktop: Todo en una línea | Mobile: Botón Volver + Título arriba, botones abajo */}
        <Box 
          display="flex" 
          flexDirection={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          gap={{ xs: 2, md: 2 }}
        >
          {/* Sección izquierda: Botón Volver + Título */}
          <Box display="flex" alignItems="flex-start" gap={{ xs: 1, sm: 2 }} flexGrow={1} minWidth={0}>
            <Button
              startIcon={<BackIcon />}
              onClick={() => navigate('/forms')}
              size="small"
              sx={{ flexShrink: 0 }}
            >
              Volver
            </Button>
            <Box flexGrow={1} minWidth={0}>
              <Typography 
                variant="h4" 
                component="h1"
                sx={{ 
                  fontSize: { xs: '1.25rem', sm: '2rem' },
                  wordBreak: 'break-word'
                }}
              >
                {formData.nombre}
              </Typography>
              <Typography 
                variant="body2" 
                color="textSecondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Vista Previa - Solo Lectura
              </Typography>
            </Box>
          </Box>

          {/* Sección derecha: Botones de Acción */}
          <Box 
            display="flex" 
            gap={1} 
            flexShrink={0}
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
          >
            <Button
              variant="outlined"
              startIcon={<PdfIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
              onClick={exportToPDF}
              size="small"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                py: { xs: 0.75, sm: 1 },
                px: { xs: 1.5, sm: 2 },
                whiteSpace: 'nowrap'
              }}
            >
              Descargar PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<EditIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
              onClick={() => navigate(`/forms/${id}/edit`)}
              size="small"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                py: { xs: 0.75, sm: 1 },
                px: { xs: 1.5, sm: 2 },
                whiteSpace: 'nowrap'
              }}
            >
              Editar
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Información General */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Información General
          </Typography>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography 
              variant="body1" 
              paragraph
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 2 }}
            >
              {formData.descripcion || 'Sin descripción'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Box 
              display="flex" 
              flexWrap="wrap" 
              gap={1}
              alignItems="center"
            >
              <Chip
                label={`Versión ${formData.version}`}
                color="primary"
                size="small"
              />
              {formData.es_template && (
                <Chip
                  label="Plantilla"
                  color="info"
                  size="small"
                />
              )}
              <Chip
                label={formData.activo ? 'Activo' : 'Inactivo'}
                color={formData.activo ? 'success' : 'default'}
                size="small"
                icon={<CheckIcon />}
              />
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
                <DescriptionIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'secondary.main', mb: 1 }} />
                <Typography variant="h4" color="secondary.main" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                  {formData.total_secciones}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                  Secciones
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
                <CategoryIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" color="primary.main" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                  {formData.total_categorias}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                  Categorías
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
                <QuestionIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'success.main', mb: 1 }} />
                <Typography variant="h4" color="success.main" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                  {formData.total_preguntas}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                  Preguntas
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
                <StarIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'warning.main', mb: 1 }} />
                <Typography variant="h4" color="warning.main" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                  {questions.filter(q => q.obligatoria).length}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                  Obligatorias
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Secciones y Preguntas */}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={2}>
          <DescriptionIcon sx={{ mr: 1, color: 'secondary.main' }} />
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Secciones y Preguntas
          </Typography>
        </Box>

        {sections.length === 0 ? (
          <Alert severity="info">
            Este formulario no tiene secciones configuradas.
          </Alert>
        ) : (
          sections.map((section) => {
            const sectionQuestions = getQuestionsBySection(section.id);

            return (
              <Accordion
                key={section.id}
                expanded={expandedCategory === section.id}
                onChange={(_e, isExpanded) => setExpandedCategory(isExpanded ? section.id : false)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }} width="100%">
                    <Box
                      sx={{
                        width: { xs: 6, sm: 8 },
                        height: { xs: 32, sm: 40 },
                        backgroundColor: section.color || '#2196F3',
                        borderRadius: 1
                      }}
                    />
                    <Box flex={1} minWidth={0}>
                      <Typography 
                        variant="subtitle1" 
                        fontWeight="bold"
                        sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          wordBreak: 'break-word'
                        }}
                      >
                        {section.orden}. {section.nombre}
                      </Typography>
                      {section.descripcion && (
                        <Typography 
                          variant="caption" 
                          color="textSecondary"
                          sx={{ 
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            display: { xs: 'none', sm: 'block' }
                          }}
                        >
                          {section.descripcion}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={`${sectionQuestions.length} pregunta${sectionQuestions.length !== 1 ? 's' : ''}`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{ 
                        display: { xs: 'none', sm: 'flex' },
                        fontSize: '0.7rem'
                      }}
                    />
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails>
                  {sectionQuestions.length === 0 ? (
                    <Alert severity="info">
                      Esta sección no tiene preguntas configuradas.
                    </Alert>
                  ) : (
                    <List>
                      {sectionQuestions.map((question, idx) => (
                        <React.Fragment key={question.id}>
                          {idx > 0 && <Divider />}
                          <ListItem
                            sx={{
                              display: 'block',
                              py: { xs: 1.5, sm: 2 },
                              px: { xs: 1, sm: 2 },
                              backgroundColor: idx % 2 === 0 ? 'background.default' : 'transparent'
                            }}
                          >
                            <Box 
                              display="flex" 
                              justifyContent="space-between" 
                              alignItems="flex-start" 
                              mb={1}
                              gap={1}
                            >
                              <Typography 
                                variant="subtitle2" 
                                fontWeight="bold"
                                sx={{ 
                                  fontSize: { xs: '0.875rem', sm: '1rem' },
                                  wordBreak: 'break-word',
                                  flex: 1
                                }}
                              >
                                {idx + 1}. {question.texto}
                              </Typography>
                              <Chip
                                label={`${question.ponderacion}%`}
                                size="small"
                                color="primary"
                                sx={{ flexShrink: 0 }}
                              />
                            </Box>

                            {question.descripcion && (
                              <Typography 
                                variant="body2" 
                                color="textSecondary" 
                                paragraph
                                sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 1.5 }}
                              >
                                {question.descripcion}
                              </Typography>
                            )}

                            {/* Información detallada de la pregunta */}
                            <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                              {/* Categoría */}
                              <Chip
                                icon={<CategoryIcon />}
                                label={getCategoryNameById(question.categoria_id)}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                              />
                              
                              {/* Tipo de pregunta */}
                              <Chip
                                label={QUESTION_TYPES[question.tipo]}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                              />
                              
                              {/* Obligatoria */}
                              {question.obligatoria && (
                                <Chip
                                  label="Obligatoria"
                                  size="small"
                                  color="warning"
                                  icon={<StarIcon />}
                                  sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                />
                              )}
                              
                              {/* Evidencia */}
                              {question.permite_evidencia && (
                                <Chip
                                  label="Evidencia"
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  icon={<ImageIcon />}
                                  sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                />
                              )}
                              
                              {/* Comentarios */}
                              {question.permite_comentario && (
                                <Chip
                                  label="Comentarios"
                                  size="small"
                                  color="default"
                                  variant="outlined"
                                  icon={<CommentIcon />}
                                  sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                />
                              )}
                            </Box>

                            {renderQuestionConfig(question)}
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })
        )}
      </Paper>
    </Box>
  );
};

export default FormPreview;
